import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Sparkles, BadgePercent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { globalMonths, useFilters } from "@/context/FilterContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { api, eur, num, errMsg } from "@/lib/api";
import { StoreGrowth } from "@/components/StoreGrowth";
import { StoreCompare } from "@/components/StoreCompare";

const COLS = [["spend", "Vendite"], ["receipts", "Scontrini"], ["avg_receipt", "Scontrino medio"], ["fidelity_share", "% fedeltà"], ["clients", "Clienti fed."], ["growth", "vs a.p."]];

const MiniChart = ({ title, data, id }) => (
  <Panel title={title} testId={id}>
    <div className="h-52 p-3">
      <ResponsiveContainer>
        <BarChart data={data}>
          <CartesianGrid stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="k" tickLine={false} axisLine={false} fontSize={11} />
          <YAxis tickLine={false} axisLine={false} fontSize={10} width={60} tickFormatter={(v) => num(v)} />
          <Tooltip formatter={(v) => eur(v)} />
          <Bar dataKey="v" name="Vendite" fill="#2563eb" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Panel>
);

const StoreDetail = ({ id, months }) => {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const nav = useNavigate();
  useEffect(() => { setD(null); setError(""); api.get(`/stores/${id}/detail`, { params: { months } }).then((r) => setD(r.data)).catch((e) => setError(errMsg(e))); }, [id, months]);
  if (error) return <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="store-detail-error">{error}</div>;
  if (!d) return <Skeleton className="h-64" />;
  const toArr = (o) => Object.entries(o || {}).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => ({ k: k.length === 7 ? k.slice(5) + "/" + k.slice(2, 4) : k, v }));
  return (
    <div className="animate-rise space-y-4" data-testid="store-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="store-detail-name">{d.name}</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="store-promo-button" onClick={() => nav(`/promozioni?scope=store&id=${id}`)}><BadgePercent className="mr-2 h-4 w-4" />Promozioni</Button>
          <Button size="sm" data-testid="store-ai-button" className="bg-blue-600 hover:bg-blue-700" onClick={() => nav(`/analisi-ia?scope=store&id=${id}`)}><Sparkles className="mr-2 h-4 w-4" />Analisi IA</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {[["Vendite", eur(d.spend)], ["Scontrini", num(d.receipts)], ["Scontrino medio", eur(d.avg_receipt)], ["Vendite fedeltà", eur(d.fidelity_spend)],
          ["Clienti fedeltà", num(d.fidelity_clients)], ["Quota promo", `${(d.promo_share || 0).toLocaleString("it-IT")}%`]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div><div className="mt-1 font-heading text-lg font-semibold">{v}</div></div>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <MiniChart id="store-monthly-chart" title="Vendite per mese" data={toArr(d.monthly_spend)} />
        <MiniChart id="store-weekday-chart" title="Vendite per giorno" data={["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((k) => ({ k, v: d.weekday_spend?.[k] || 0 }))} />
        <MiniChart id="store-hour-chart" title="Vendite per ora" data={toArr(d.hour_spend)} />
      </div>
      <StoreGrowth id={id} />
      <Panel title="Prodotti trainanti" testId="store-products">
        {d.top_products.length === 0 ? <Empty text="Nessun dato prodotto." /> : d.top_products.slice(0, 15).map((p) => (
          <div key={p.name} className="flex justify-between border-b border-slate-100 px-5 py-2 text-sm last:border-0"><span className="truncate">{p.name}</span><span className="font-mono text-xs">{eur(p.spend)}</span></div>
        ))}
      </Panel>
    </div>
  );
};

export default function Stores() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const { filters } = useFilters();
  const gm = globalMonths(filters, [1, 3, 6, 12]);
  const months = gm.months;
  const [sort, setSort] = useState("spend");
  const [sel, setSel] = useState(null);
  useEffect(() => { setRows(null); api.get("/stores/ranking", { params: { months } }).then((r) => setRows(r.data)).catch((e) => setError(errMsg(e))); }, [months]);
  const sorted = [...(rows || [])].sort((a, b) => (b[sort] ?? -Infinity) - (a[sort] ?? -Infinity));

  return (
    <div>
      <PageHeader title="Punti vendita" subtitle={rows ? `${rows.length} punti vendita con vendite nel periodo` : "Caricamento..."}>
        <span data-testid="stores-period-label" className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600">Periodo: {gm.label}</span>
      </PageHeader>
      {error && <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="stores-error">{error}</div>}
      <div className="[&>*]:mt-0 [&>*]:mb-6"><StoreCompare months={months} /></div>
      <div className="grid gap-6 2xl:grid-cols-[1.5fr_1fr] [&>*]:min-w-0">
        <Panel title="Classifica" testId="stores-ranking">
          {!rows ? <Skeleton className="m-4 h-64" /> : rows.length === 0 ? <Empty text="Nessun punto vendita." /> : (
            <div className="max-h-[720px] overflow-auto text-[13px]">
              <Table data-testid="stores-table">
                <TableHeader><TableRow><TableHead>#</TableHead><TableHead className="min-w-[180px]">Punto vendita</TableHead>
                  {COLS.map(([k, l]) => <TableHead key={k} className="cursor-pointer text-right" data-testid={`stores-sort-${k}`} onClick={() => setSort(k)}><span className={sort === k ? "text-blue-600" : ""}>{l}</span></TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {sorted.map((r, i) => (
                    <TableRow key={r.id} data-testid={`store-row-${r.id}`} onClick={() => setSel(r.id)} className={`cursor-pointer ${sel === r.id ? "bg-blue-50/60" : ""}`}>
                      <TableCell className="font-mono text-xs text-slate-400">{i + 1}</TableCell>
                      <TableCell className="min-w-[180px] whitespace-normal break-words text-sm font-medium leading-tight" title={r.name}>{r.name}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{eur(r.spend)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{num(r.receipts)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{eur(r.avg_receipt)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.fidelity_share.toLocaleString("it-IT")}%</TableCell>
                      <TableCell className="text-right font-mono text-xs">{num(r.clients)}</TableCell>
                      <TableCell className="text-right font-mono text-xs" data-testid={`store-growth-${r.id}`}>
                        {r.growth === null || r.growth === undefined ? <span className="text-slate-300">—</span>
                          : <span className={`rounded px-1.5 py-0.5 ${r.growth >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{r.growth > 0 ? "+" : ""}{r.growth.toFixed(1).replace(".", ",")}%</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Panel>
        <div>{sel ? <StoreDetail id={sel} months={months} /> : <Panel><Empty text="Seleziona un punto vendita dalla classifica per vederne il dettaglio." /></Panel>}</div>
      </div>
    </div>
  );
}
